'use client';

import {
  Stack,
  Box,
  Typography,
  MenuItem,
  IconButton,
  Select,
  styled,
  Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { GridCallbackDetails, GridPaginationModel } from '@mui/x-data-grid-pro';

export const PAGE_SIZE_OPTIONS = [10, 25, 50];

const PaginationSelect = styled(Select)(({ theme }) => ({
  borderRadius: 5,
  color: theme.palette.info.main,
  [`&:hover`]: {
    borderColor: theme.palette.info.main,
  },
  [`& .MuiInputBase-input`]: {
    paddingBlock: 1,
    border: 0,
  },
}));

type GalleryPaginationProps = {
  rowCount: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange?: (
    model: GridPaginationModel,
    details: GridCallbackDetails<'pagination'>,
  ) => void;
};

export default function GalleryPagination({
  rowCount,
  paginationModel,
  onPaginationModelChange,
}: GalleryPaginationProps) {
  const page = paginationModel.page;
  const pageSize = paginationModel.pageSize;
  const pageCount = Math.ceil(rowCount / pageSize);

  const handlePageChange = (newPage: number) => {
    if (onPaginationModelChange) {
      onPaginationModelChange({ page: newPage, pageSize }, {
        reason: 'pagination',
        api: {} as any,
      } as unknown as GridCallbackDetails<'pagination'>);
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    if (onPaginationModelChange) {
      onPaginationModelChange({ page: 0, pageSize: newPageSize }, {
        reason: 'pagination',
        api: {} as any,
      } as unknown as GridCallbackDetails<'pagination'>);
    }
  };

  return (
    <Stack direction={'row'} gap={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="labelmedium" sx={{ paddingInlineEnd: 2 }}>
          Rows per page:
        </Typography>
        <PaginationSelect
          color="info"
          value={pageSize}
          onChange={(event) => handlePageSizeChange(Number(event.target.value))}
          sx={{
            [`& .MuiInputBase-input`]: {
              paddingBlock: 1,
            },
          }}
        >
          {PAGE_SIZE_OPTIONS.map((option, index) => (
            <MenuItem key={index} value={option}>
              {option}
            </MenuItem>
          ))}
        </PaginationSelect>
      </Box>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="labelmedium" sx={{ paddingInlineEnd: 2 }}>
          {pageSize * page + 1}-{Math.min(pageSize * (page + 1), rowCount)} of{' '}
          {rowCount}
        </Typography>
        <Box>
          <IconButton
            color="info"
            size="small"
            disabled={page <= 0}
            onClick={() => handlePageChange(page - 1)}
          >
            <ArrowBackIcon />
          </IconButton>
          <IconButton
            color="info"
            size="small"
            onClick={() => handlePageChange(page + 1)}
            disabled={page >= pageCount - 1}
          >
            <ArrowForwardIcon />
          </IconButton>
        </Box>
      </Box>
    </Stack>
  );
}
